import React, { useCallback, useContext, useRef, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { Button } from '../../components/Button';
import { Container } from '../../components/Container';
import { OfferPopup } from '../../components/OfferPopup';
import { VisibleOn } from '../../components/VisibleOn';
import { VisibleOnVersion } from '../../components/VisibleOnVersion';
import { useStoreData } from '../../hooks';
import { CoreContext, WindowContext } from '../../providers';
import { parseHTML } from '../../services/parseHTML';
import { replaceShortcodes } from '../../services/replaceShortcodes';
import { Storyblok, StoryblokSection } from '../../storyblok';
import { GraphWrapper, ScrollIndicator, Section, Wrapper } from './style';

function useHookWithRefCallback() {
    const ref = useRef(null);
    const setRef = useCallback((node) => {
        if (ref.current) {
            // Make sure to cleanup any events/references added to the last instance
        }

        if (node) {
            // Check if a node is actually passed. Otherwise node would be null.
            // You can now do what you need to, addEventListeners, measure, etc.
            node.scrollLeft = node.scrollWidth - node.clientWidth;
        }

        // Save a reference to the node
        ref.current = node;
    }, []);

    return [setRef];
}

export const ScoreGraph = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ScoreGraph>) => {
    const { openPopup, settings } = useContext(CoreContext);

    const { isMobile } = useContext(WindowContext);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);

    const { scoreData } = useStoreData().user;

    const [graphRef] = useHookWithRefCallback();

    if (
        !sectionData ||
        !scoreData ||
        !scoreData.bestScore ||
        !scoreData.trends
    ) {
        return null;
    }

    const { bestScore, trends } = scoreData;

    const {
        button_label_negative: buttonLabelNegative,
        button_label_positive: buttonLabelPositive,
        title_negative: titleNegative,
        title_positive: titlePositive,
    } = sectionData;

    const graphData = Object.entries(bestScore).map(([key, value]) => ({
        year: key,
        score: value,
    })) as any;

    /**
     * We cannot really draw a graph if there would be only one year entry.
     * So if the `graphData.length < 2` we simply return null
     */
    if (!graphData || graphData.length < 2) {
        return null;
    }

    const scoreDifference =
        graphData[graphData.length - 1].score -
        graphData[graphData.length - 2].score;

    const handleScroll = (event: any) => {
        if (!isMobile()) {
            return;
        }

        /** @type {Element} */
        const graphWrapper = event.target;

        const maxWrapperScrollSize =
            graphWrapper.scrollWidth - graphWrapper.clientWidth;

        if (graphWrapper.scrollLeft === maxWrapperScrollSize) {
            setShowScrollIndicator(true);
        } else {
            setShowScrollIndicator(false);
        }
    };

    return (
        <Section>
            <Container sizeGroup="size-2">
                <Wrapper>
                    {parseHTML(
                        replaceShortcodes(
                            scoreDifference >= 0
                                ? titlePositive
                                : titleNegative,
                            { difference: scoreDifference },
                        ),
                        'markdown',
                    )}
                    <GraphWrapper ref={graphRef} onScroll={handleScroll}>
                        <ResponsiveContainer
                            width="99%"
                            height={isMobile() ? 250 : 650}
                        >
                            <LineChart
                                data={graphData}
                                margin={
                                    isMobile()
                                        ? {
                                              top: 5,
                                              right: -10,
                                              left: 20,
                                              bottom: 5,
                                          }
                                        : {
                                              top: 5,
                                              right: 30,
                                              left: -20,
                                              bottom: 5,
                                          }
                                }
                            >
                                <defs>
                                    <linearGradient
                                        id="colorUv"
                                        x1="0%"
                                        y1="0%"
                                        x2="0%"
                                        y2="100%"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor={
                                                settings.languageVersion ===
                                                'pengestyrke'
                                                    ? '#133143'
                                                    : '#54ef93'
                                            }
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor={
                                                settings.languageVersion ===
                                                'pengestyrke'
                                                    ? '#133143'
                                                    : '#15d8e2'
                                            }
                                        />
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="year" />
                                <YAxis
                                    type="number"
                                    dataKey="score"
                                    domain={[
                                        (dataMin: any) =>
                                            Math.round(dataMin / 10) * 10 - 10,
                                        (dataMax: any) =>
                                            Math.round(dataMax / 10) * 10 + 10,
                                    ]}
                                    orientation={isMobile() ? 'right' : 'left'}
                                />
                                <CartesianGrid />
                                <Tooltip />
                                <Line
                                    type="monotone"
                                    dataKey="score"
                                    stroke="url(#colorUv)"
                                    strokeWidth="8"
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </GraphWrapper>
                    <VisibleOnVersion versions={['upscore']}>
                        {trends.offer && (
                            <Button
                                variant="primary"
                                onClick={() =>
                                    openPopup(<OfferPopup {...trends.offer} />)
                                }
                                data-event="popup trigger button (comparision graph button)"
                            >
                                {scoreDifference >= 0
                                    ? buttonLabelPositive
                                    : buttonLabelNegative}
                            </Button>
                        )}
                    </VisibleOnVersion>

                    <VisibleOn mobile tablet>
                        <ScrollIndicator
                            className={`${showScrollIndicator && 'active'}`}
                        >
                            Swipe for å se flere
                        </ScrollIndicator>
                    </VisibleOn>
                </Wrapper>
            </Container>
        </Section>
    );
};
